import { Button, FormControl, FormField, FormItem, FormLabel, Input } from '@kalos/ui';

import { OPTION_ALL } from '../../../../constants';
import { useAuth } from '../../../../context/AuthContext';
import { useFeatureFlag } from '../../../../featureFlags/useFeatureFlag';
import { useOrganizationGroupListQuery } from '../../../../hooks/react-query/useOrgStructureQuery';
import { orgChartAdminCheck } from '../../../../tools/permissionChecks';
import {
  QuerySelectPickerV2,
  TimesheetDepartmentPickerV2,
} from '../../Pickers/newPickers/QueryPickerV2';
import { TechniciansComboboxPicker } from '../../Pickers/newPickers/TechnicianComboboxPicker';
import { useEmployeeSearchFormContext } from './utils';

type EmployeeSearchFormProps = { showOwnedItemField?: boolean };
const EmployeeSearchForm: React.FC<EmployeeSearchFormProps> = ({ showOwnedItemField = true }) => {
  const form = useEmployeeSearchFormContext();
  // Privilege 116. Both filters arrived with this work, so both are gated: the
  // Team one reads a structure that is empty for anyone who cannot maintain it,
  // and the supervisor one is new here even though managed_by itself is not.
  // The Assigned Supervisor field on the employee record stays visible to
  // everyone — that is where the value is set and it long predates this.
  /*
    Behind org-chart.module along with the rest of the feature. The permission
    and the flag are read separately because a hook cannot be called
    conditionally — and because the two mean different things: one is who may
    use this, the other is whether it exists yet.
  */
  const hasOrgChartPermission = orgChartAdminCheck(useAuth().user);
  const orgChartModuleOn = useFeatureFlag('org-chart.module', {
    applies: hasOrgChartPermission,
  });
  const isOrgChartAdmin = hasOrgChartPermission && orgChartModuleOn;

  return (
    // intentionally ignored as values are used as they are typed in
    <form
      className="grid grid-cols-2 place-content-center gap-x-4 gap-y-3 md:grid-cols-3 lg:grid-cols-5"
      onSubmit={form.handleSubmit(console.log)}
    >
      <FormField
        control={form.control}
        name="firstname"
        render={({ field }) => {
          return (
            <FormItem>
              <FormLabel>First Name</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          );
        }}
      />
      <FormField
        control={form.control}
        name="lastname"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Last Name</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="employeeDepartmentID"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Department</FormLabel>
            <FormControl>
              <TimesheetDepartmentPickerV2
                placeholder={OPTION_ALL}
                renderItem={(department) => `${department.value} - ${department.description}`}
                selected={field.value?.toString() ?? ''}
                onSelect={(department) => field.onChange(department?.id ?? 0)}
                queryArgs={{
                  filter: {
                    isActive: 1,
                  },
                }}
              />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="empTitle"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Title</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="email"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="cellphone"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Cell</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="phone"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Office</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Badge #</FormLabel>
            <FormControl>
              <Input type="number" {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      {isOrgChartAdmin ? (
        <>
          <FormField
            control={form.control}
            name="orgTeamId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Team</FormLabel>
                <FormControl>
                  {/*
                The same picker the other query-backed filters use, so it looks
                like them — a hand-rolled select does not inherit the styling.
              */}
                  <QuerySelectPickerV2
                    query={useOrganizationGroupListQuery}
                    queryArgs={{}}
                    placeholder={OPTION_ALL}
                    // Teams only. A group is a box on a team's page, not
                    // something a person is "on" in directory terms, and offering
                    // all thirty of them buried the eighteen that mean something.
                    // Picking a team matches everyone in its groups too.
                    filter={(team) => team.isActive && !team.parentGroupId}
                    sort={(a, b) =>
                      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
                    }
                    renderItem={(team) => team.name}
                    selected={field.value ? String(field.value) : ''}
                    onSelect={(team) => {
                      const teamId = team?.id ?? 0;
                      field.onChange(teamId);
                      // Selecting a team IS the filter; the orgFilter param exists
                      // for the links off the Manage Organization warning line.
                      form.setValue('orgFilter', teamId ? 'team' : 'none');
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="managedBy"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Supervisor</FormLabel>
                <FormControl>
                  <TechniciansComboboxPicker
                    placeholder={OPTION_ALL}
                    onSelect={(user) => field.onChange(user?.id ?? 0)}
                    selected={field.value ? String(field.value) : ''}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </>
      ) : null}
      {showOwnedItemField && (
        <FormField
          control={form.control}
          name="employeeItemName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Owned Item name</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />
      )}
      <Button
        type="reset"
        variant="outline"
        className="mt-auto max-w-max"
        onClick={() => form.reset()}
      >
        Reset
      </Button>
    </form>
  );
};

export default EmployeeSearchForm;
